import { useState } from 'react';
import {
  Store,
  Clock,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  Truck,
  Shield,
  Save,
  Camera,
  Globe,
  Bell,
  Image,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface CafeProfile {
  name_ar: string;
  name_en: string;
  description_ar: string;
  description_en: string;
  phone: string;
  email: string;
  address: string;
  area: string;
  openingTime: string;
  closingTime: string;
  deliveryFee: number;
  minOrder: number;
  maxDeliveryDistance: number;
  isOpen: boolean;
  autoAcceptOrders: boolean;
  notifyNewOrders: boolean;
  notifyReviews: boolean;
  preparationBuffer: number;
}

export default function CafeSettings() {
  const { language } = useTranslation();
  const [activeTab, setActiveTab] = useState<'profile' | 'delivery' | 'hours' | 'notifications'>('profile');
  const [saved, setSaved] = useState(false);

  const [profile, setProfile] = useState<CafeProfile>({
    name_ar: 'مقهى الورد',
    name_en: 'Rose Cafe',
    description_ar: 'أفضل مقهى في مصر الجديدة. نقدم أجود أنواع القهوة والحلويات منذ 2020.',
    description_en: 'The best cafe in Heliopolis. Serving premium coffee and desserts since 2020.',
    phone: '01012345000',
    email: 'rose@cafe.com',
    address: 'القاهرة، مصر الجديدة، شارع النزهة 15',
    area: 'مصر الجديدة',
    openingTime: '07:00',
    closingTime: '23:00',
    deliveryFee: 15,
    minOrder: 50,
    maxDeliveryDistance: 10,
    isOpen: true,
    autoAcceptOrders: false,
    notifyNewOrders: true,
    notifyReviews: true,
    preparationBuffer: 5,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { key: 'profile' as const, icon: Store, label: { ar: 'الملف الشخصي', en: 'Profile' } },
    { key: 'delivery' as const, icon: Truck, label: { ar: 'التوصيل', en: 'Delivery' } },
    { key: 'hours' as const, icon: Clock, label: { ar: 'ساعات العمل', en: 'Working Hours' } },
    { key: 'notifications' as const, icon: Bell, label: { ar: 'الإشعارات', en: 'Notifications' } },
  ];

  const days = language === 'ar'
    ? ['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة']
    : ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {language === 'ar' ? 'إعدادات المقهى' : 'Cafe Settings'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? 'إدارة معلومات وإعدادات المقهى' : 'Manage cafe information and settings'}
          </p>
        </div>
        <button
          onClick={handleSave}
          className={`px-6 py-2.5 rounded-xl font-semibold flex items-center gap-2 shadow-lg transition-all ${
            saved
              ? 'bg-emerald-600 text-white shadow-emerald-200 dark:shadow-emerald-900/30'
              : 'bg-amber-600 text-white hover:bg-amber-700 shadow-amber-200 dark:shadow-amber-900/30'
          }`}
        >
          <Save size={18} />
          {saved
            ? (language === 'ar' ? 'تم الحفظ ✓' : 'Saved ✓')
            : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${
              activeTab === tab.key
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/30'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-amber-300'
            }`}
          >
            <tab.icon size={16} />
            {tab.label[language]}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="p-6 space-y-6">
            {/* Cafe Logo/Banner */}
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center text-5xl">
                  ☕
                </div>
                <button className="absolute -bottom-2 -end-2 w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center hover:bg-amber-700 transition-colors shadow-lg">
                  <Camera size={14} />
                </button>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-800 dark:text-white">{profile.name_ar}</h3>
                <p className="text-sm text-slate-500">{profile.name_en}</p>
                <button className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1">
                  <Image size={14} />
                  {language === 'ar' ? 'تغيير صورة الغلاف' : 'Change cover image'}
                </button>
              </div>
            </div>

            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'اسم المقهى (عربي)' : 'Cafe Name (Arabic)'}
                </label>
                <input
                  type="text"
                  value={profile.name_ar}
                  onChange={(e) => setProfile({ ...profile, name_ar: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'اسم المقهى (إنجليزي)' : 'Cafe Name (English)'}
                </label>
                <input
                  type="text"
                  value={profile.name_en}
                  onChange={(e) => setProfile({ ...profile, name_en: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
                </label>
                <textarea
                  value={profile.description_ar}
                  onChange={(e) => setProfile({ ...profile, description_ar: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none resize-none"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
                </label>
                <textarea
                  value={profile.description_en}
                  onChange={(e) => setProfile({ ...profile, description_en: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none resize-none"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  <Phone size={14} className="inline me-1" />
                  {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  <Mail size={14} className="inline me-1" />
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                <MapPin size={14} className="inline me-1" />
                {language === 'ar' ? 'العنوان' : 'Address'}
              </label>
              <input
                type="text"
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Map Placeholder */}
            <div className="h-48 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <div className="text-center">
                <MapPin size={32} className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">{language === 'ar' ? 'خريطة الموقع' : 'Location Map'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Tab */}
        {activeTab === 'delivery' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  <DollarSign size={14} className="inline me-1" />
                  {language === 'ar' ? 'رسوم التوصيل (ج.م)' : 'Delivery Fee (EGP)'}
                </label>
                <input
                  type="number"
                  value={profile.deliveryFee}
                  onChange={(e) => setProfile({ ...profile, deliveryFee: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  <DollarSign size={14} className="inline me-1" />
                  {language === 'ar' ? 'الحد الأدنى للطلب (ج.م)' : 'Minimum Order (EGP)'}
                </label>
                <input
                  type="number"
                  value={profile.minOrder}
                  onChange={(e) => setProfile({ ...profile, minOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  <Truck size={14} className="inline me-1" />
                  {language === 'ar' ? 'أقصى مسافة (كم)' : 'Max Distance (km)'}
                </label>
                <input
                  type="number"
                  value={profile.maxDeliveryDistance}
                  onChange={(e) => setProfile({ ...profile, maxDeliveryDistance: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                <Clock size={14} className="inline me-1" />
                {language === 'ar' ? 'وقت إضافي للتحضير (دقائق)' : 'Preparation Buffer (minutes)'}
              </label>
              <input
                type="number"
                value={profile.preparationBuffer}
                onChange={(e) => setProfile({ ...profile, preparationBuffer: parseInt(e.target.value) || 0 })}
                className="w-full max-w-xs px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-slate-400 mt-1">
                {language === 'ar'
                  ? 'وقت إضافي يُضاف لوقت التحضير المقدر للعميل'
                  : 'Extra time added to the estimated preparation time shown to customers'}
              </p>
            </div>

            {/* Toggle: Auto Accept */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
              <div className="flex items-center gap-3">
                <Shield size={20} className="text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'قبول الطلبات تلقائياً' : 'Auto-accept Orders'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {language === 'ar'
                      ? 'قبول الطلبات الجديدة تلقائياً بدون مراجعة'
                      : 'Automatically accept new orders without manual review'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setProfile({ ...profile, autoAcceptOrders: !profile.autoAcceptOrders })}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  profile.autoAcceptOrders ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-md absolute top-1 transition-all ${
                  profile.autoAcceptOrders ? 'end-1' : 'start-1'
                }`} />
              </button>
            </div>

            {/* Toggle: Cafe Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
              <div className="flex items-center gap-3">
                <Store size={20} className="text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'حالة المقهى' : 'Cafe Status'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {profile.isOpen
                      ? (language === 'ar' ? 'المقهى مفتوح ويستقبل الطلبات' : 'Cafe is open and accepting orders')
                      : (language === 'ar' ? 'المقهى مغلق حالياً' : 'Cafe is currently closed')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setProfile({ ...profile, isOpen: !profile.isOpen })}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  profile.isOpen ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-md absolute top-1 transition-all ${
                  profile.isOpen ? 'end-1' : 'start-1'
                }`} />
              </button>
            </div>
          </div>
        )}

        {/* Working Hours Tab */}
        {activeTab === 'hours' && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {language === 'ar'
                ? 'حدد ساعات العمل لكل يوم من أيام الأسبوع'
                : 'Set working hours for each day of the week'}
            </p>

            {days.map((day, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <span className="w-28 font-semibold text-slate-700 dark:text-slate-300">{day}</span>
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="time"
                    defaultValue={index === 6 ? '10:00' : profile.openingTime}
                    className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                  <span className="text-slate-400">—</span>
                  <input
                    type="time"
                    defaultValue={index === 6 ? '18:00' : profile.closingTime}
                    className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <button
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                >
                  {language === 'ar' ? 'مفتوح' : 'Open'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {language === 'ar'
                ? 'إدارة إعدادات الإشعارات'
                : 'Manage notification preferences'}
            </p>

            {[
              {
                key: 'notifyNewOrders',
                icon: <Store size={20} className="text-amber-600 dark:text-amber-400" />,
                title: { ar: 'طلبات جديدة', en: 'New Orders' },
                desc: { ar: 'إشعار عند استلام طلب جديد', en: 'Notify when a new order is received' },
                value: profile.notifyNewOrders,
              },
              {
                key: 'notifyReviews',
                icon: <Globe size={20} className="text-blue-600 dark:text-blue-400" />,
                title: { ar: 'تقييمات جديدة', en: 'New Reviews' },
                desc: { ar: 'إشعار عند إضافة تقييم جديد', en: 'Notify when a new review is posted' },
                value: profile.notifyReviews,
              },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3">
                  {item.icon}
                  <div>
                    <p className="font-semibold text-slate-800 dark:text-white">{item.title[language]}</p>
                    <p className="text-xs text-slate-500">{item.desc[language]}</p>
                  </div>
                </div>
                <button
                  onClick={() => setProfile({ ...profile, [item.key]: !item.value })}
                  className={`w-12 h-7 rounded-full transition-colors relative ${
                    item.value ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-md absolute top-1 transition-all ${
                    item.value ? 'end-1' : 'start-1'
                  }`} />
                </button>
              </div>
            ))}

            {/* Sound */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'صوت الإشعارات' : 'Notification Sound'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {language === 'ar' ? 'تشغيل صوت عند استلام إشعارات' : 'Play sound on notifications'}
                  </p>
                </div>
              </div>
              <select className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 outline-none">
                <option>{language === 'ar' ? 'الافتراضي' : 'Default'}</option>
                <option>{language === 'ar' ? 'صوت عالي' : 'Loud'}</option>
                <option>{language === 'ar' ? 'هادئ' : 'Quiet'}</option>
                <option>{language === 'ar' ? 'بدون صوت' : 'Silent'}</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
